<template>
  <div>
    <title-header
      title="Parks 3 Step"
      subtitle="Isolate the paretic muscle in acquired vertical diplopia"
      image="https://www.eyedock.com/images/menu_icons/parks_3_step_icon.svg"
      documentationPage="/documentation/parks-3-step"
      :loading="loading"
    ></title-header>
    <v-alert v-if="error" type="error" class="mt-4" dismissible
      >Error loading data...</v-alert
    >
    <div class="">
      <div
        class="d-inline-block d-sm-flex align-center justify-space-between width100p"
      >
        <div class="ma-2 ma-sm-4">
          <div class="eyeImgDiv my-3">
            <img :src="imageForSize" class="eyeImg" />
            <div v-for="eye in ['r', 'l']" :key="eye">
              <div v-for="eom in eoms" :key="eom">
                <v-expand-transition>
                  <div
                    class="crossout"
                    :style="styleForCrossout(eye, eom)"
                    v-if="eliminated.includes(eye + eom)"
                  ></div>
                </v-expand-transition>
                <v-fade-transition>
                  <div
                    class="circleIt"
                    v-if="eye + eom == remaining"
                    :style="styleForCircle(eye, eom)"
                  ></div>
                </v-fade-transition>
              </div>
            </div>
          </div>

          <v-card class="questionDiv mt-4 pa-3" flat color="#d1ecff">
            <div
              v-for="(qObj, qKey) in questions"
              :key="qKey"
              class="d-flex flex-row flex-wrap my-2 justify-sm-space-between justify-center align-center"
            >
              <div class="font-weight-medium questionText mb-2">
                {{ qObj.question }}
              </div>
              <div class="my-2">
                <!-- {{ qObj }} -->

                <v-btn
                  v-for="btnOption in ['r', 'l']"
                  :key="qKey + btnOption"
                  small
                  depressed
                  class="mx-2 btnClass"
                  :color="qObj.answer == btnOption ? 'primary' : null"
                  @click="answerQuestion(qKey, btnOption)"
                >
                  <v-img
                    :src="eyeImgPath + qKey + '_' + btnOption + '.svg'"
                    class="mr-1"
                    contain
                    height="25"
                    width="20"
                  />
                  {{ qObj[btnOption] }}
                </v-btn>
              </div>
            </div>
          </v-card>
          <!-- {{ eliminated }}<br />{{ remaining }} -->
        </div>
        <see-also
          class="d-none d-sm-flex pt-6"
          referrer="SITE"
          :identifier="relatedICDs"
        ></see-also>
      </div>
      <div>
        <div class="title text-decoration-underline mt-4">Result</div>
        <v-card class="questionDiv pa-3" flat color="#fff0d2">
          <v-fade-transition>
            <div v-if="remaining">
              The only EOM to not be eliminated is the
              <span class="title" style="color: #0052aa">
                {{ remainingEyeAndEomString | titleize }} ({{
                  remaining.toUpperCase()
                }})
              </span>
            </div>
            <div v-else>(Answer the 3 questions to see the result)</div>
          </v-fade-transition>
        </v-card>
      </div>

      <v-fade-transition>
        <div v-if="remaining">
          <v-expansion-panels class="my-6" multiple flat>
            <v-expansion-panel>
              <v-expansion-panel-header class="expansionHeader"
                >Explaining the Result - Why the
                {{ remainingEyeAndEomString | titleize }}?
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-card
                  v-for="(qObj, qKey) in questions"
                  :key="qKey + '2'"
                  flat
                  color="#fffcf4"
                  class="mr-1 mr-sm-4 my-4 discussionCard"
                >
                  <div class="d-flex flex-wrap align-center px-4">
                    <span class="font-weight-bold"> {{ qObj.question }}</span>
                  </div>
                  <v-card-text class="darkerCardText">
                    <div class="d-flex flex-wrap">
                      <v-img
                        :src="eyeImgPath + discussion[qKey][qObj.answer].img"
                        contain
                        :max-width="$vuetify.breakpoint.smAndUp ? 330 : '100%'"
                      />
                      <div class="pa-4" style="max-width: 500px">
                        <div class="blue--text caption">
                          Your answer: {{ qObj[qObj.answer] }}
                        </div>
                        <div
                          v-html="discussion[qKey][qObj.answer].text"
                          class="mt-2"
                        ></div>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header class="expansionHeader">
                {{ remainingEomString | titleize }} Palsy - Differentials
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div
                  class="mt-3"
                  v-for="(bullets, key) in differentials[remainingEomAbbr]"
                  :key="key"
                >
                  <div class="font-weight-medium">
                    {{ key | titleize }} Causes
                  </div>
                  <ul>
                    <li v-for="(bullet, i) in bullets" :key="i">
                      <vue-markdown>{{ bullet }}</vue-markdown>
                    </li>
                  </ul>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header class="expansionHeader">
                {{ remainingEomString | titleize }} Palsy - Next Steps
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <ul class="mt-3">
                  <li
                    v-for="(step, i) in next_steps[remainingEomAbbr]"
                    :key="'ns' + i"
                  >
                    <vue-markdown>{{ step }}</vue-markdown>
                  </li>
                </ul>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel>
              <v-expansion-panel-header class="expansionHeader">
                {{ remainingEomString | titleize }} Palsy - Torsion
                Considerations
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <p class="mt-3">
                  With a paretic <b>{{ remainingEomString }}</b
                  >, you would expect to see
                  <b>
                    {{ remainingEyeString }}
                    eye
                    {{ expectedTorsion[remainingEomAbbr].torsion }}</b
                  >
                  ({{ expectedTorsion[remainingEomAbbr].reason }}).
                </p>
                <p>
                  There are multiple ways to assess torsion, including double
                  Maddox rod testing, fundus photo analysis, or OCT analysis.
                  The Torsion Assessment Utility below can assist you with
                  Maddox rod testing, which is a widely used subjective method
                  for evaluating torsion.
                </p>
                <p>
                  If the torsion findings are inconsistent with the identified
                  muscle, it may suggest an alternative diagnosis, such as
                  <b>skew deviation</b> or another neurological condition.
                </p>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </v-fade-transition>

      <!--    <v-fade-transition>
        <div v-if="remaining" class="mb-6">
          <div class="title mt-6 text-decoration-underline">Discussion</div>
        <div class="discussion d-flex flex-row flex-wrap">
            <v-card
              v-for="(qObj, qKey) in questions"
              :key="qKey + '2'"
              :width="$vuetify.breakpoint.smAndUp ? 350 : '100%'"
              flat
              color="#fffcf4"
              class="mr-1 mr-sm-4 mb-4 discussionCard"
            >
              <div class="pa-4">
                <span class="font-weight-bold"> {{ qObj.question }}</span>
                <div class="blue--text caption">
                  Your answer: {{ qObj[qObj.answer] }}
                </div>
              </div>
              <v-card-text class="darkerCardText">
                <v-img
                  :src="eyeImgPath + discussion[qKey][qObj.answer].img"
                  contain
                  :width="$vuetify.breakpoint.smAndUp ? 330 : '100%'"
                />
                <div
                  v-html="discussion[qKey][qObj.answer].text"
                  class="mt-2"
                ></div>
              </v-card-text>
            </v-card>
          </div> 
          <div class="title mt-6 text-decoration-underline">Next Steps</div>
          <v-card class="pa-3 discussionCard" flat color="#fffcf4">
            <p>
              After identifying the paretic extraocular muscle (EOM) using the
              Parks 3-step test,
              <b
                >it is recommended to evaluate the torsion of the affected
                eye.</b
              >
              By checking torsion, you can verify whether the observed torsional
              pattern matches the expected findings for the paretic muscle.
            </p>
            <p>
              <span class="highlight">
                With a paretic <b>{{ muscleNames[remaining] }}</b
                >, you would expect to see
                <b>
                  {{ remainingMuscleComponents.eye == "r" ? "right" : "left" }}
                  eye
                  {{ expectedTorsion[remainingMuscleComponents.eom].torsion }}</b
                ></span
              >
              ({{ expectedTorsion[remainingMuscleComponents.eom].reason }}).
            </p>
            <p>
              There are multiple ways to assess torsion, including double Maddox
              rod testing, fundus photo analysis, or OCT analysis. The tool
              below can assist you with Maddox rod testing, which is a widely
              used subjective method for evaluating torsion.
            </p>
            <p>
              If the torsion findings are inconsistent with the identified
              muscle, it may suggest an alternative diagnosis, such as
              <b>skew deviation</b> or another neurological condition.
            </p>
          </v-card>
        </div>
      </v-fade-transition>
-->
      <cyclotorsion class="mt-8">
        <v-fade-transition>
          <div v-if="remaining" class="my-4 ml-2 highlight">
            Parks 3-step test suggests a
            <b>{{ remainingEyeAndEomString | titleize }}</b> palsy. Expected
            torsion is
            <b
              >{{ remainingEyeString }} eye

              {{ expectedTorsion[remainingEomAbbr].torsion }}</b
            >
          </div>
        </v-fade-transition>
      </cyclotorsion>
    </div>
    <see-also
      class="d-block d-sm-none mt-8"
      referrer="SITE"
      :identifier="relatedICDs"
    ></see-also>
  </div>
</template>

<script>
import Cyclotorsion from "./Cyclotorsion.vue";
import TitleHeader from "../universalStuff/TitleHeader.vue";
import SeeAlso from "../universalStuff/SeeAlso.vue";
import GetJsonMixin from "@/assets/js/GetJsonMixin";
import VueMarkdown from "@adapttive/vue-markdown";

export default {
  name: "Parks3step",
  components: {
    Cyclotorsion,
    TitleHeader,
    SeeAlso,
    VueMarkdown,
  },
  mixins: [GetJsonMixin],
  props: {},
  data: function () {
    return {
      loading: false,
      error: null,
      eyeImgPath: "https://www.eyedock.com/images/parks3step/",
      relatedICDs: ["H49", "H51.8", "H51.2"],
      eyeImage: "parks_eoms",
      eoms: ["so", "sr", "io", "ir"],
      allEoms: ["rso", "rsr", "rir", "rio", "lso", "lsr", "lir", "lio"],
      muscleNames: {
        so: "Superior Oblique",
        sr: "Superior Rectus",
        ir: "Inferior Rectus",
        io: "Inferior Oblique",
      },
      eliminated: [],
      remaining: null,
      crossouts: {
        so: { x: 1, y: 9 },
        sr: { x: 1, y: 25 },
        io: { x: 1, y: 71 },
        ir: { x: 1, y: 83 },
      },
      questions: {
        primary: {
          question: "Which eye is hyper deviated in primary gaze?",
          r: "Right Hyper",
          l: "Left Hyper",
          answer: null,
        },
        gaze: {
          question:
            "Is the vertical deviation greater in right gaze or left gaze?",
          r: "Right Gaze",
          l: "Left Gaze",
          answer: null,
        },
        tilt: {
          question:
            "Is the vertical deviation greater with right head tilt or left head tilt?",
          r: "Right Tilt",
          l: "Left Tilt",
          answer: null,
        },
      },
      eomsToEliminate: {
        primary: {
          r: ["rsr", "rio", "lso", "lir"],
          l: ["rso", "rir", "lsr", "lio"],
        },
        gaze: {
          r: ["rso", "rio", "lsr", "lir"],
          l: ["rsr", "rir", "lso", "lio"],
        },
        tilt: {
          r: ["rir", "rio", "lso", "lsr"],
          l: ["rso", "rsr", "lio", "lir"],
        },
      },
      discussion: null, // will be imported from json
      expectedTorsion: null, // will be imported from json
      differentials: null, // will be imported from json
      next_steps: null, // will be imported from json

      // discussion: {
      //   primary: {
      //     r: {
      //       img: "eom-parks-step-1-right.svg",
      //       text: "If the right eye is the hyperdeviated eye we know that muscle that is failing is either responsible for pulling the right eye down or for pulling the left eye up. Our suspects in the right eye are the RSO or RIR. In the left eye it’s the LSR or LIO. <br><p>We can eliminate all others as possibilities.</p>",
      //     },
      //     l: {
      //       img: "eom-parks-step-1-left.svg",
      //       text: "If the left eye is the hyperdeviated eye we know that muscle that is failing is either responsible for pulling the left eye down or for pulling the right eye up. Our suspects in the right eye are the RSR or RIO. In the left eye it's the LSO or LIR. <br><p>We can eliminate all others as possibilities.</p>",
      //     },
      //   },
      //   gaze: {
      //     r: {
      //       img: "eom-parks-step-2-right-gaze.svg",
      //       text: "When we look in right gaze, the muscles that control the vertical alignment are the right rectus muscles and the left oblique muscles. Therefore our suspects are RSR, RIR, LSO, LIO. <br><p>The other four can be eliminated.</p>",
      //     },
      //     l: {
      //       img: "eom-parks-step-2-left-gaze.svg",
      //       text: "When we look in left gaze, the muscles that control the vertical alignment are the left rectus muscles and the right oblique muscles. Therefore our suspects are LSR, LIR, RSO, RIO. <br><p>The other four can be eliminated.</p>",
      //     },
      //   },
      //   tilt: {
      //     r: {
      //       img: "eom-parks-step-3-right-tilt.svg",
      //       text: "When the head is tilted to the right, the right eye needs to turn inward (intorsion) and the left eye needs to turn outward (extorsion). The muscles responsible for these movements are the RSR, RSO and the  LIO, LIR. One of these could be malfunctioning. <br><p>The other four can be eliminated.</p>",
      //     },
      //     l: {
      //       img: "eom-parks-step-3-left-tilt.svg",
      //       text: "When the head is tilted to the left, the right eye needs to turn outward (extorsion) and the left eye needs to turn inward (intorsion). The muscles responsible for these movements are the RIO, RIR and the LSO, LSR. One of these could be malfunctioning. <br><p>The other four can be eliminated.</p>",
      //     },
      //   },
      // },
      // expectedTorsion: {
      //   so: {
      //     torsion: "extorsion",
      //     reason:
      //       "The superior oblique muscle is an intorter, so when it is weak the eye will extort due to unopposed action of the inferior oblique.",
      //   },
      //   io: {
      //     torsion: "intorsion",
      //     reason:
      //       "The inferior oblique muscle is an extorter, so when it is weak the eye will intort due to unopposed action of the superior oblique.",
      //   },
      //   sr: {
      //     torsion: "intorsion",
      //     reason:
      //       "The superior rectus muscle contributes to extorsion in addition to elevation. When it is weak, the unopposed action of the superior oblique causes intorsion.",
      //   },
      //   ir: {
      //     torsion: "extorsion",
      //     reason:
      //       "The inferior rectus muscle contributes to intorsion in addition to depression. When it is weak, the unopposed action of the inferior oblique causes extorsion.",
      //   },
      // },
    };
  },
  methods: {
    styleForCrossout(eye, eom) {
      let rtn = {};
      let dir = eye == "r" ? "left" : "right";
      let width = this.$vuetify.breakpoint.smAndUp ? "20%" : "5%";
      rtn[dir] = this.crossouts[eom].x + "%";
      rtn.top = this.crossouts[eom].y + "%";
      rtn.width = width;
      return rtn;
    },
    styleForCircle(eye, eom) {
      let rtn = {};
      let dir = eye == "r" ? "left" : "right";
      let width = this.$vuetify.breakpoint.smAndUp ? "22%" : "10%";
      rtn[dir] = this.crossouts[eom].x - 2 + "%";
      rtn.top = this.crossouts[eom].y - 6 + "%";
      rtn.width = width;
      rtn.height = "13%";
      // console.log(rtn);
      return rtn;
    },
    answerQuestion(qKey, answer) {
      //   console.log("qNum", qNum, "answer", answer);
      this.questions[qKey].answer = answer;
      this.updateEliminated();
    },
    updateEliminated() {
      let eliminated = [];
      //   let primary = this.questions.primary.answer;
      //   let gaze = this.questions.gaze.answer;
      //     let tilt = this.questions.tilt.answer;
      ["primary", "gaze", "tilt"].forEach((qKey) => {
        let qObj = this.questions[qKey];
        let answer = qObj.answer;
        if (answer) {
          let eomsToEliminate = this.eomsToEliminate[qKey][answer];
          eomsToEliminate.forEach((eom) => {
            if (!eliminated.includes(eom)) eliminated.push(eom);
          });
        }
      });
      this.eliminated = eliminated;
      let remainingMuscles = this.allEoms.filter(
        (x) => !eliminated.includes(x)
      );

      // this.arr_diff(eliminated, this.allEoms);
      if (remainingMuscles.length == 1) this.remaining = remainingMuscles[0];
    },

    // takes an eye and muscle abbreviation (eg "rso") and returns an object with eye and EOM (eg {eye: "r", eom: "so"})
    splitEyeAndMuscleAbbr(abbr) {
      let eye = abbr.charAt(0); // 'r' or 'l'
      let eom = abbr.slice(1); // 'so', 'sr', 'io', 'ir'
      return {
        eye: eye,
        eom: eom,
      };
    },
  },
  computed: {
    imageForSize: function () {
      return (
        this.eyeImgPath +
        this.eyeImage +
        (this.$vuetify.breakpoint.smAndUp ? "" : "_mobile") +
        ".svg"
      );
    },
    remainingEyeAbbr: function () {
      if (!this.remaining) return null;
      return this.splitEyeAndMuscleAbbr(this.remaining).eye;
    },
    remainingEomAbbr: function () {
      if (!this.remaining) return null;
      return this.splitEyeAndMuscleAbbr(this.remaining).eom;
    },
    remainingEyeString: function () {
      if (!this.remainingEyeAbbr) return null;
      // let letter = this.splitEyeAndMuscleAbbr(this.remaining).eye;
      return this.remainingEyeAbbr == "r" ? "Right" : "Left";
    },
    remainingEomString: function () {
      if (!this.remainingEomAbbr) return null;
      // let eom = this.splitEyeAndMuscleAbbr(this.remaining).eom;
      return this.muscleNames[this.remainingEomAbbr];
    },
    remainingEyeAndEomString: function () {
      if (!this.remaining) return null;
      // let { eye, eom } = this.splitEyeAndMuscleAbbr(this.remaining);
      return `${this.remainingEyeString} ${this.remainingEomString}`;
    },
  },
  mounted() {
    this.getJSON("parks3step", "json", [
      "discussion",
      "expectedTorsion",
      "differentials",
      "next_steps",
    ]); // in the mixin
  },
  metaInfo() {
    return {
      title: "EyeDock - Parks 3 Step and Cyclotorsion",
    };
  },
};
</script>

<style scoped>
.eyeImgDiv {
  position: relative;
  max-width: 100%;
  width: 700px;
  margin: 0 auto;
}
.eyeImg {
  width: 100%;
}
.crossout {
  position: absolute;
  height: 2px;
  background-color: red;
}
.circleIt {
  position: absolute;
  /*background-color: red;*/
  border-radius: 50%;
  border: 3px solid red;
  /*z-index: -1;*/
}
.questionDiv {
  max-width: 100%;
  /* width: 800px; */
}
.btnClass {
  min-width: 140px !important;
}
.questionText {
  /* max-width: 300px; */
}
.discussionCard {
  border: 1px solid #eae5d8 !important;
}
span.highlight {
  background-color: yellow;
}
.expansionHeader {
  background-color: #fffcf4;
  border: 1px solid #eae5d8;
  font-weight: bold;
}
</style>
